import { useState } from "react";
import axios from "axios";
import { useAuthContext } from "./useAuthContext";
import { useAccountContext } from "./useAccountContext";
import { useSettingsContext } from "./useSettingsContext";

export const useLeaveSchemes = () => {
  const { username, user, clientApi, dispatch: authDispatch } = useAuthContext();
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { account } = useAccountContext();
  const { isWinAuth } = useSettingsContext();

  const axiosInstance = axios.create({
    baseURL: `${clientApi}`,
    headers: {
      accepts: "application/json",
      "Content-Type": "application/json",
    },
  });

  axiosInstance.interceptors.request.use(
    (request) => {
      const accessToken = user.token;
      if (accessToken) {
        request.headers["Authorization"] = `Bearer ${accessToken}`;
      }
      return request;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (
        error.response &&
        error.response.status === 401 &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;
        try {
          if (!isWinAuth) {
            // Make a request to your auth server to refresh the token.
            const response = await axiosInstance.post(`${clientApi}api/auth/refresh`, user);
            // Store the new access and refresh tokens.
            authDispatch({ type: 'Refresh', payload: response.data, clientApi: clientApi, username: username });

            // Update the authorization header with the new access token.
            axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
          }
          else {
            const newToken = await acquireSilentToken();
            let tmpUser = user;
            tmpUser.token = newToken;
            authDispatch({ type: 'REFRESH', payload: tmpUser, clientApi: clientApi, username: username });
            axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          }
          return axios(originalRequest);
        } catch (refreshError) {
          console.error("Token refresh failed:", refreshError);
          return Promise.reject(refreshError);
        }
      }
      return Promise.reject(error);
    }
  );

  const getLeaveTypes = async (selectedCompany) => {
    if (!selectedCompany) return [];
    setIsLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Company/GetLeaveTypesSchemes`,
        {
          headers: { Authorization: `Bearer ${user.token}` },
          params: { company: selectedCompany },
        }
      );

      return response.data;
    } catch (err) {
      console.error("Error fetching leave types data:", err);
      setError(err);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getLeaveSchemesRules = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Company/GetLeaveSchemesRules`,
        {
          headers: { Authorization: `Bearer ${user.token}` }
        }
      );
      return response.data;
    } catch (err) {
      console.error("Error fetching rules", err);
      setError(err);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getLeaveSchemesCriteria = async (selectedCompany) => {
    if (!selectedCompany) return [];
    setIsLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Company/GetLeaveSchemesCriteria`,
        {
          headers: { Authorization: `Bearer ${user.token}` },
          params: { company: selectedCompany },
        }
      );


      return response.data;
    } catch (err) {
      console.error("Error fetching criteria", err);
      setError(err);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const postRecreateLeaveAnalysisForEmployees = async (selectedCompany) => {
    if (!selectedCompany) return [];
    setIsLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/CreateLeaveAnalysisAllEmployees`, null,
        {
          params: { company: selectedCompany, username: username, userId: account.employeeId, isClear: true },
        }
      );
      console.log(response.data)
      return response.data;
    } catch (err) {
      console.error("Error fetching params", err);
      setError(err);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getLeaveSchemesParams = async (selectedCompany, acode) => {
    if (!selectedCompany) return [];
    setIsLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Company/GetLeaveSchemesParams`,
        {
          headers: { Authorization: `Bearer ${user.token}` },
          params: { company: selectedCompany, acode: acode },
        }
      );
      return response.data;
    } catch (err) {
      console.error("Error fetching params", err);
      setError(err);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getLeaveSchemesWorkUnits = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Company/GetLeaveSchemesWorkUnits`,
        {
          headers: { Authorization: `Bearer ${user.token}` }
        }
      );
      return response.data;
    } catch (err) {
      console.error("Error fetching work units", err);
      setError(err);
      return [];
    } finally {
      setIsLoading(false);
    }
  };


  const insertLeaveScheme = async (scheme) => {

    try {
      await axiosInstance.post(`${clientApi}api/Company/InsertLeaveScheme`, scheme, {
        headers: { Authorization: `Bearer ${user.token}` },
        params: { employeeid: account.employeeId }
      });
      return true;
    } catch (err) {
      console.error('Error inserting Leave Scheme:', err.message);
      return false;
    }
  };

  const deleteLeaveScheme = async (rowguid) => {

  try {
    await axiosInstance.delete(`${clientApi}api/Company/DeleteLeaveScheme`, {
      headers: { Authorization: `Bearer ${user.token}` },
      params: { rowguid: rowguid, employeeid: account.employeeId }
    });
    return true;
  } catch (err) {
    console.error('Error deleting Leave Scheme:', err.message);
    return false;
  }
};
  

  const getLeaveSchemesEmployeesIncludedTwice = async (aCode, company) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Company/GetEmployeesIncludedTwice`,
        {
          headers: { Authorization: `Bearer ${user.token}` },
          params: { aCode: aCode, company: company }
        }
      );
      return response.data;
    } catch (err) {
      console.error("Error fetching employees included twice", err);
      setError(err);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getLeaveSchemesEmployeesNotIncluded = async (aCode, company) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Company/GetEmployeesNotIncluded`,
        {
          headers: { Authorization: `Bearer ${user.token}` },
          params: { aCode: aCode, company: company }
        }
      );
      return response.data;
    } catch (err) {
      console.error("Error fetching not included", err);
      setError(err);
      return [];
    } finally {
      setIsLoading(false);
    }
  };



  return {
    error, isLoading, getLeaveTypes, getLeaveSchemesRules,
    getLeaveSchemesCriteria, getLeaveSchemesWorkUnits, insertLeaveScheme, deleteLeaveScheme,
    getLeaveSchemesEmployeesIncludedTwice, getLeaveSchemesEmployeesNotIncluded, getLeaveSchemesParams, postRecreateLeaveAnalysisForEmployees
  };
};
